import Ajv, { type ErrorObject } from 'ajv';

// Moments are timeline points where the video lands — not clips, not regions,
// not slideshow scenes. The plan describes a continuous arc punctuated by N
// moments; the agent then designs what happens around each moment with motion
// between them.
export interface PlanMoment {
    index: number;
    label: string;
    voiceover: string;
    start: number;
    duration: number;
    // Set at plan time when this moment should carry the site's real look by recreating one of its
    // signature frames (from a <website_analysis> `frames` index), rather than being authored from
    // scratch. The agent recreates the frame's look itself, inline, when authoring. `name` is the
    // frame to borrow; `brief` is the pattern within it + the content to fill it.
    brandFrame?: { name: string; brief: string };
}

// Design tokens + mood/direction. The agent picks these to serve the editorial
// intent (warm + serif for finance, energetic + sans for product, etc.) and
// then references them via CSS variables throughout the composition.
export interface PlanStyle {
    description: string; // Visual direction prose — what the video looks like and how its elements behave.
    mood: string; // Tone descriptor (e.g. "professional, decisive"). Separate field so a future UI can offer a dropdown.
    colors: {
        primary: string;
        accent: string;
        bg: string;
        text: string;
        textMuted: string;
        surface: string;
    };
    fonts: {
        display: string; // CSS font shorthand for titles / hero text
        body: string; // CSS font shorthand for body copy
    };
    motion: {
        enter: string; // GSAP easing for entrances
        emphasis: string; // GSAP easing for emphasis hits
        durationS: number; // Default tween duration in seconds
    };
}

// Optional background-music intent the agent commits to at plan time. When
// present, the commit_plan result tells the agent to run search_music({ query })
// + set_music after authoring. When absent, the agent keeps its post-authoring
// autonomy on whether to add any.
export interface PlanMusicIntent {
    query: string;
}

// Resolved voiceover voice the agent commits at plan time. Unlike music (a
// free-text intent), this is an actual voice resolved via search_voices, so the
// plan card can show its real name. Absent when the video has no narration.
export interface PlanVoiceIntent {
    slug: string;
    displayName: string;
    traits?: string; // human-readable summary, e.g. "Female · British · Calm"
}

export interface PlanManifest {
    version: number;
    // The video's format/genre slug (e.g. "explainer", "data-story", or
    // "bespoke") — the archetype the agent classified into before planning. See
    // formats/formatRegistry. The slug is validated against the registry at
    // commit time (CommitPlanTool); the schema here only checks it's present and
    // non-empty so this module stays free of the registry dependency.
    format: string;
    moments: PlanMoment[];
    style: PlanStyle;
    music?: PlanMusicIntent;
    voice?: PlanVoiceIntent;
}

export const PLAN_MANIFEST_VERSION = 1;

const roundTime = (n: number): number => Math.round(n * 100) / 100;

/**
 * Snap the plan's moments to the voiceover's real per-moment spans. The plan's
 * `start`/`duration` are planning-time estimates; the composition's clips
 * follow create_voiceover's `moment_spans` (the commit_plan result instructs
 * the agent to set clip windows from them). Without retiming, layout probes land
 * mid-estimate — often inside a *different* moment's actual window — and
 * findings get attributed to the wrong moment.
 *
 * Each moment holds from its span's start until the next span starts; the last
 * runs to the audio's end (or its own span end, whichever is later). Only
 * applies when every plan moment matches a span 1:1 in order — a plan with
 * visual-only moments (no span) keeps its estimates rather than guessing how
 * the spoken spans share time with the silent ones. Returns null when not
 * applicable.
 */
export function retimeMomentsFromSpans(
    moments: PlanMoment[],
    spans: Array<{ label: string; start: number; end: number }>,
    audioDurationS: number,
): PlanMoment[] | null {
    if (moments.length === 0 || spans.length !== moments.length) return null;
    if (moments.some((m, i) => m.label !== spans[i].label)) return null;
    if (spans.some((s, i) => i > 0 && s.start < spans[i - 1].end)) return null;

    return moments.map((m, i) => {
        const start = spans[i].start;
        const end =
            i + 1 < spans.length ? spans[i + 1].start : Math.max(spans[i].end, audioDurationS);
        return { ...m, start: roundTime(start), duration: roundTime(end - start) };
    });
}

export type ValidationResult = { ok: true } | { ok: false; errors: string[] };

// Shape/type validation via JSON Schema. Cross-field rules (sequential moment
// indices, monotonic non-overlapping moments) aren't expressible in vanilla
// JSON Schema and stay as small manual passes below.
const NON_EMPTY_STRING = { type: 'string', minLength: 1 } as const;
const PLAN_MANIFEST_SCHEMA = {
    type: 'object',
    required: ['version', 'format', 'moments', 'style'],
    properties: {
        version: { const: PLAN_MANIFEST_VERSION },
        format: NON_EMPTY_STRING,
        moments: {
            type: 'array',
            minItems: 1,
            items: {
                type: 'object',
                required: ['index', 'label', 'voiceover', 'start', 'duration'],
                properties: {
                    index: { type: 'integer', minimum: 1 },
                    label: NON_EMPTY_STRING,
                    voiceover: { type: 'string' }, // "" is allowed for visual-only moments
                    start: { type: 'number', minimum: 0 },
                    duration: { type: 'number', exclusiveMinimum: 0 },
                    brandFrame: {
                        type: 'object',
                        required: ['name', 'brief'],
                        properties: { name: NON_EMPTY_STRING, brief: NON_EMPTY_STRING },
                    },
                },
            },
        },
        style: {
            type: 'object',
            required: ['description', 'mood', 'colors', 'fonts', 'motion'],
            properties: {
                description: NON_EMPTY_STRING,
                mood: NON_EMPTY_STRING,
                colors: {
                    type: 'object',
                    required: ['primary', 'accent', 'bg', 'text', 'textMuted', 'surface'],
                    properties: {
                        primary: NON_EMPTY_STRING,
                        accent: NON_EMPTY_STRING,
                        bg: NON_EMPTY_STRING,
                        text: NON_EMPTY_STRING,
                        textMuted: NON_EMPTY_STRING,
                        surface: NON_EMPTY_STRING,
                    },
                },
                fonts: {
                    type: 'object',
                    required: ['display', 'body'],
                    properties: {
                        display: NON_EMPTY_STRING,
                        body: NON_EMPTY_STRING,
                    },
                },
                motion: {
                    type: 'object',
                    required: ['enter', 'emphasis', 'durationS'],
                    properties: {
                        enter: NON_EMPTY_STRING,
                        emphasis: NON_EMPTY_STRING,
                        durationS: { type: 'number', exclusiveMinimum: 0 },
                    },
                },
            },
        },
        music: {
            type: 'object',
            required: ['query'],
            properties: {
                query: NON_EMPTY_STRING,
            },
        },
        voice: {
            type: 'object',
            required: ['slug', 'displayName'],
            properties: {
                slug: NON_EMPTY_STRING,
                displayName: NON_EMPTY_STRING,
                traits: { type: 'string' },
            },
        },
    },
} as const;

const ajv = new Ajv({ allErrors: true });
const validateSchema = ajv.compile(PLAN_MANIFEST_SCHEMA);

function formatAjvError(e: ErrorObject): string {
    // "/moments/2/voiceover" → "moments[2].voiceover"
    const path = e.instancePath
        .split('/')
        .filter(Boolean)
        .map((seg) => (/^\d+$/.test(seg) ? `[${seg}]` : `.${seg}`))
        .join('')
        .replace(/^\./, '');
    const where = path || 'manifest';
    return `${where}: ${e.message ?? 'is invalid'}`;
}

export function validatePlanManifest(input: unknown): ValidationResult {
    if (!validateSchema(input)) {
        return {
            ok: false,
            errors: (validateSchema.errors ?? []).map(formatAjvError),
        };
    }
    const m = input as PlanManifest;
    const errors: string[] = [];

    // Cross-field rule: moment indices must be sequential 1..N.
    m.moments.forEach((moment, i) => {
        const expected = i + 1;
        if (moment.index !== expected) {
            errors.push(
                `moments[${i}].index: ${moment.index} (indices must be sequential 1..N, expected ${expected})`,
            );
        }
    });

    // Cross-field rule: moments must not overlap. 1ms tolerance for floating-
    // point arithmetic (e.g. 4.5 + 2.3 = 6.800000000000001 would otherwise read
    // as overlap with a moment starting at 6.8).
    let prevEnd = -Infinity;
    m.moments.forEach((moment, i) => {
        if (moment.start + 0.001 < prevEnd) {
            errors.push(
                `moments[${i}]: overlaps the previous moment (starts at ${moment.start}s, previous ended at ${prevEnd}s)`,
            );
        }
        prevEnd = moment.start + moment.duration;
    });

    return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
